// Cart functionality
// Handles all cart-related operations including AJAX add/remove/update

// --- Configuration and Selectors ---
const cartSelectors = {
  cartTrigger: "#ajax-cart-trigger",
  miniCartDrawer: "#mini-cart-drawer",
  miniCartItemsWrapper: "#mini-cart-items-wrapper",
  cartItemCountBadge: "#cart-item-count",
  drawerItemCount: "#drawer-item-count",
  drawerSubtotal: "#drawer-subtotal",
  quickAddButtons: "[data-product-id]",
  removeButtons: ".ajax-remove-item",
  cartForm: "#cart-form",
  updateButtons: "[name='update']",
  checkoutButton: "[name='checkout']"
};

// --- Core AJAX Functions ---

/**
 * Fetches the current cart data from Shopify's API
 * @returns {Promise<object>} The current cart object
 */
async function getCart() {
  console.log('Cart: Fetching cart data...');
  const response = await fetch("/cart.js");
  const cart = await response.json();
  console.log('Cart: Cart data fetched:', cart);
  return cart;
}

/**
 * Updates the UI based on the new cart data
 * @param {object} cart - The new cart object from /cart.js
 */
async function updateCartUI(cart) {
  console.log('Cart: Updating UI with new data...');

  // 1. Update the header badge immediately for a responsive feel
  const badge = document.querySelector(cartSelectors.cartItemCountBadge);
  if (badge) {
    badge.textContent = cart.item_count;
    console.log('Cart: Header badge updated to:', cart.item_count);
  }

  // 2. Fetch the re-rendered mini-cart HTML using the Section Rendering API
  try {
    const response = await fetch('/cart?section_id=header'); // Assumes the mini-cart is rendered within the header section
    const text = await response.text();
    const html = new DOMParser().parseFromString(text, 'text/html');

    // Find the new content in the fetched HTML
    const newItemsWrapper = html.querySelector('#mini-cart-items-wrapper');
    const newDrawerCount = html.querySelector('#drawer-item-count');
    const newDrawerSubtotal = html.querySelector('#drawer-subtotal');

    // 3. Update the drawer's content with the fresh HTML
    const itemsWrapper = document.querySelector(cartSelectors.miniCartItemsWrapper);
    if (itemsWrapper && newItemsWrapper) {
      itemsWrapper.innerHTML = newItemsWrapper.innerHTML;
    }

    const drawerCount = document.querySelector(cartSelectors.drawerItemCount);
    if (drawerCount && newDrawerCount) {
      drawerCount.innerHTML = newDrawerCount.innerHTML;
    }

    const drawerSubtotal = document.querySelector(cartSelectors.drawerSubtotal);
    if (drawerSubtotal && newDrawerSubtotal) {
      drawerSubtotal.innerHTML = newDrawerSubtotal.innerHTML;
    }

    console.log('Cart: Drawer content updated via Section Rendering API.');

  } catch (error) {
    console.error('Cart: Error updating cart items via Section Rendering API:', error);
    // Provide a fallback message in case of an error
    const itemsWrapper = document.querySelector(cartSelectors.miniCartItemsWrapper);
    if (itemsWrapper) {
        itemsWrapper.innerHTML = '<p class="text-center text-red-500">Error updating cart.</p>';
    }
  }
}

/**
 * Handles adding a product to the cart
 * @param {string} variantId - The ID of the product variant to add
 * @param {number} quantity - The quantity to add (default 1)
 */
async function addToCart(variantId, quantity = 1) {
  console.log('Cart: Adding to cart - Variant:', variantId, 'Quantity:', quantity);

  const formData = {
    items: [
      {
        id: variantId,
        quantity: quantity,
      },
    ],
  };

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      console.log('Cart: Item added successfully');
      const cart = await getCart();
      updateCartUI(cart);

      // Open the Mini-Cart Drawer
      window.dispatchEvent(new CustomEvent('open-mini-cart'));

      return true;
    } else {
      const error = await response.json();
      console.error("Cart: Error adding to cart:", error);
      alert(`Could not add item: ${error.message}`);
      return false;
    }
  } catch (error) {
    console.error("Cart: Network or other error:", error);
    return false;
  }
}

/**
 * Handles removing an item from the cart
 * @param {string} lineItemKey - The key of the line item to remove
 */
async function removeItem(lineItemKey) {
  console.log('Cart: Removing item with key:', lineItemKey);

  const formData = {
    line: 1,
    id: lineItemKey,
    quantity: 0,
  };

  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      console.log('Cart: Item removed successfully');
      const cart = await getCart();
      updateCartUI(cart);
      return true;
    } else {
      console.error("Cart: Error removing item");
      return false;
    }
  } catch (error) {
    console.error("Cart: Network or other error:", error);
    return false;
  }
}

/**
 * Updates cart item quantity
 * @param {string} lineItemKey - The key of the line item
 * @param {number} quantity - New quantity
 */
async function updateCartItem(lineItemKey, quantity) {
  console.log('Cart: Updating item quantity - Key:', lineItemKey, 'Quantity:', quantity);

  const formData = {
    line: 1,
    id: lineItemKey,
    quantity: quantity,
  };

  try {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (response.ok) {
      console.log('Cart: Item quantity updated successfully');
      const cart = await getCart();
      updateCartUI(cart);
      return true;
    } else {
      console.error("Cart: Error updating item quantity");
      return false;
    }
  } catch (error) {
    console.error("Cart: Network or other error:", error);
    return false;
  }
}

// --- Event Listeners and Initialization ---

function initCart() {
  console.log('Cart: Initializing cart functionality');

  // Initial cart load
  getCart().then(updateCartUI);

  // Quick Add functionality
  document.querySelectorAll(cartSelectors.quickAddButtons).forEach((button) => {
    button.addEventListener("click", async (event) => {
      event.preventDefault();
      const variantId = button.dataset.variantId;
      const quantity = 1;
      console.log("Cart: Quick add triggered for variant:", variantId);
      await addToCart(variantId, quantity);
    });
  });

  // Cart trigger functionality
  const cartTrigger = document.querySelector(cartSelectors.cartTrigger);
  if (cartTrigger) {
    cartTrigger.addEventListener("click", (event) => {
      event.preventDefault();
      console.log('Cart: Cart trigger clicked');
      window.dispatchEvent(new CustomEvent('open-mini-cart'));
    });
  }

  // Remove Item functionality
  const miniCartDrawer = document.querySelector(cartSelectors.miniCartDrawer);
  if (miniCartDrawer) {
    miniCartDrawer.addEventListener("click", async (event) => {
      const removeButton = event.target.closest(cartSelectors.removeButtons);
      if (removeButton) {
        event.preventDefault();
        const lineItemKey = removeButton.dataset.lineItemKey;
        await removeItem(lineItemKey);
      }
    });
  }

  // Cart form updates (for cart page)
  const cartForm = document.querySelector(cartSelectors.cartForm);
  if (cartForm) {
    cartForm.addEventListener("submit", async (event) => {
      event.preventDefault();
      const formData = new FormData(cartForm);
      const updates = formData.getAll("updates[]");

      console.log('Cart: Updating cart with quantities:', updates);

      // Handle cart updates here
      // This would need more complex logic for updating multiple items
    });
  }

  console.log('Cart: Cart initialization complete');
}

// Initialize cart when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCart);
} else {
  initCart();
}

// Export functions for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    getCart,
    updateCartUI,
    addToCart,
    removeItem,
    updateCartItem,
    initCart
  };
}